import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { useRBAC } from '@/contexts/RBACContext';
import { 
  Menu, Plus, Edit2, Trash2, ChevronDown, ChevronRight, 
  GripVertical, Settings, Save, X, LayoutDashboard, Users,
  Building, Megaphone, MessageSquare, BarChart3, Database, Folder
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const iconOptions = [
  { value: 'layout-dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'building', label: 'Building', icon: Building },
  { value: 'megaphone', label: 'Megaphone', icon: Megaphone },
  { value: 'message-square', label: 'Message', icon: MessageSquare },
  { value: 'bar-chart-3', label: 'Chart', icon: BarChart3 },
  { value: 'database', label: 'Database', icon: Database },
  { value: 'settings', label: 'Settings', icon: Settings },
  { value: 'folder', label: 'Folder', icon: Folder },
];

const MenuManagement = () => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState({});
  
  // Menu dialog state
  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuForm, setMenuForm] = useState({
    menu_code: '',
    menu_name: '',
    icon: 'folder',
    display_order: 1,
    status: 'active'
  });
  
  // Submenu dialog state
  const [showSubmenuDialog, setShowSubmenuDialog] = useState(false);
  const [editingSubmenu, setEditingSubmenu] = useState(null);
  const [parentMenuId, setParentMenuId] = useState(null);
  const [submenuForm, setSubmenuForm] = useState({
    sub_menu_code: '',
    sub_menu_name: '',
    route_path: '',
    icon: 'file',
    display_order: 1,
    status: 'active'
  });

  const token = sessionStorage.getItem('auth_token');
  // RBACContext holds the sidebar navigation in React state. When admins
  // rename / add / delete a menu here, the backend's per-user nav cache
  // gets invalidated (see enterprise_rbac_service.py update_menu), but
  // the frontend never re-fetches on its own until the next login. We
  // call refreshNavigation() after every write so the sidebar updates
  // live — no logout/login round-trip.
  const { refreshNavigation } = useRBAC();

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/admin/menus?token=${token}&include_inactive=true`);
      if (response.data.success) {
        setMenus(response.data.menus);
        // Auto-expand all menus
        const expanded = {};
        response.data.menus.forEach(m => expanded[m.id] = true);
        setExpandedMenus(expanded);
      }
    } catch (error) {
      toast.error('Failed to load menus');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (menuId) => {
    setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  // Menu CRUD
  const openMenuDialog = (menu = null) => {
    if (menu) {
      setEditingMenu(menu);
      setMenuForm({
        menu_code: menu.menu_code,
        menu_name: menu.menu_name,
        icon: menu.icon || 'folder',
        display_order: menu.display_order,
        status: menu.status
      });
    } else {
      setEditingMenu(null);
      setMenuForm({
        menu_code: '',
        menu_name: '',
        icon: 'folder',
        display_order: menus.length + 1,
        status: 'active'
      });
    }
    setShowMenuDialog(true);
  };

  const saveMenu = async () => {
    try {
      if (editingMenu) {
        await axios.put(`${API}/api/admin/menus/${editingMenu.id}?token=${token}`, menuForm);
        toast.success('Menu updated');
      } else {
        await axios.post(`${API}/api/admin/menus?token=${token}`, menuForm);
        toast.success('Menu created');
      }
      setShowMenuDialog(false);
      fetchMenus();
      refreshNavigation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save menu');
    }
  };

  const deleteMenu = async (menuId) => {
    if (!window.confirm('Delete this menu and all its submenus?')) return;
    try {
      await axios.delete(`${API}/api/admin/menus/${menuId}?token=${token}`);
      toast.success('Menu deleted');
      fetchMenus();
      refreshNavigation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete menu');
    }
  };

  // Submenu CRUD
  const openSubmenuDialog = (menuId, submenu = null) => {
    setParentMenuId(menuId);
    if (submenu) {
      setEditingSubmenu(submenu);
      setSubmenuForm({
        sub_menu_code: submenu.sub_menu_code.split('.').pop(),
        sub_menu_name: submenu.sub_menu_name,
        route_path: submenu.route_path,
        icon: submenu.icon || 'file',
        display_order: submenu.display_order,
        status: submenu.status
      });
    } else {
      setEditingSubmenu(null);
      const menu = menus.find(m => m.id === menuId);
      setSubmenuForm({
        sub_menu_code: '',
        sub_menu_name: '',
        route_path: '',
        icon: 'file',
        display_order: (menu?.sub_menus?.length || 0) + 1,
        status: 'active'
      });
    }
    setShowSubmenuDialog(true);
  };

  const saveSubmenu = async () => {
    try {
      if (editingSubmenu) {
        await axios.put(`${API}/api/admin/submenus/${editingSubmenu.id}?token=${token}`, submenuForm);
        toast.success('Submenu updated');
      } else {
        await axios.post(`${API}/api/admin/menus/${parentMenuId}/submenus?token=${token}`, submenuForm);
        toast.success('Submenu created');
      }
      setShowSubmenuDialog(false);
      fetchMenus();
      refreshNavigation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save submenu');
    }
  };

  const deleteSubmenu = async (submenuId) => {
    if (!window.confirm('Delete this submenu?')) return;
    try {
      await axios.delete(`${API}/api/admin/submenus/${submenuId}?token=${token}`);
      toast.success('Submenu deleted');
      fetchMenus();
      refreshNavigation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete submenu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="menu-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Menu Management</h1>
          <p className="text-slate-600">Configure navigation menus and submenus</p>
        </div>
        <Button onClick={() => openMenuDialog()} data-testid="add-menu-btn">
          <Plus size={16} className="mr-2" />
          Add Menu
        </Button>
      </div>

      {/* Menu List */}
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-12"></TableHead>
              <TableHead>Menu / Submenu</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Route</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {menus.map((menu) => (
              <React.Fragment key={menu.id}>
                {/* Menu Row */}
                <TableRow className="bg-slate-50/50 hover:bg-slate-100">
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(menu.id)}
                      className="p-1 h-6 w-6"
                    >
                      {expandedMenus[menu.id] ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 font-medium">
                      <Menu size={16} className="text-primary" />
                      {menu.menu_name}
                      <Badge variant="outline" className="text-xs">
                        {menu.sub_menus?.length || 0} items
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">
                    {menu.menu_code}
                  </TableCell>
                  <TableCell className="text-slate-400">-</TableCell>
                  <TableCell>{menu.display_order}</TableCell>
                  <TableCell>
                    <Badge variant={menu.status === 'active' ? 'default' : 'secondary'}>
                      {menu.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openSubmenuDialog(menu.id)}
                        title="Add Submenu"
                      >
                        <Plus size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openMenuDialog(menu)}
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMenu(menu.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>

                {/* Submenu Rows */}
                {expandedMenus[menu.id] && menu.sub_menus?.map((submenu) => (
                  <TableRow key={submenu.id} className="hover:bg-slate-50">
                    <TableCell></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 pl-6">
                        <GripVertical size={12} className="text-slate-300" />
                        {submenu.sub_menu_name}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">
                      {submenu.sub_menu_code}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-blue-600">
                      {submenu.route_path}
                    </TableCell>
                    <TableCell>{submenu.display_order}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={submenu.status === 'active' ? 'outline' : 'secondary'}
                        className="text-xs"
                      >
                        {submenu.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openSubmenuDialog(menu.id, submenu)}
                        >
                          <Edit2 size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSubmenu(submenu.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Menu Dialog */}
      <Dialog open={showMenuDialog} onOpenChange={setShowMenuDialog}>
        <DialogContent className="max-w-md" data-testid="menu-dialog">
          <DialogHeader>
            <DialogTitle>{editingMenu ? 'Edit Menu' : 'Add Menu'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Menu Code</Label>
              <Input
                value={menuForm.menu_code}
                onChange={(e) => setMenuForm({ ...menuForm, menu_code: e.target.value })}
                placeholder="e.g., CRM, REAL_ESTATE"
                disabled={!!editingMenu}
              />
            </div>
            <div className="space-y-2">
              <Label>Menu Name</Label>
              <Input
                value={menuForm.menu_name}
                onChange={(e) => setMenuForm({ ...menuForm, menu_name: e.target.value })}
                placeholder="Display name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={menuForm.icon}
                  onValueChange={(value) => setMenuForm({ ...menuForm, icon: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon size={14} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={menuForm.display_order}
                  onChange={(e) => setMenuForm({ ...menuForm, display_order: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={menuForm.status}
                onValueChange={(value) => setMenuForm({ ...menuForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMenuDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveMenu}>
              <Save size={14} className="mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submenu Dialog */}
      <Dialog open={showSubmenuDialog} onOpenChange={setShowSubmenuDialog}>
        <DialogContent className="max-w-md" data-testid="submenu-dialog">
          <DialogHeader>
            <DialogTitle>{editingSubmenu ? 'Edit Submenu' : 'Add Submenu'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Submenu Code</Label>
              <Input
                value={submenuForm.sub_menu_code}
                onChange={(e) => setSubmenuForm({ ...submenuForm, sub_menu_code: e.target.value })}
                placeholder="e.g., CONTACTS, DASHBOARD"
                disabled={!!editingSubmenu}
              />
            </div>
            <div className="space-y-2">
              <Label>Submenu Name</Label>
              <Input
                value={submenuForm.sub_menu_name}
                onChange={(e) => setSubmenuForm({ ...submenuForm, sub_menu_name: e.target.value })}
                placeholder="Display name"
              />
            </div>
            <div className="space-y-2">
              <Label>Route Path</Label>
              <Input
                value={submenuForm.route_path}
                onChange={(e) => setSubmenuForm({ ...submenuForm, route_path: e.target.value })}
                placeholder="/contacts"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={submenuForm.display_order}
                  onChange={(e) => setSubmenuForm({ ...submenuForm, display_order: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={submenuForm.status}
                  onValueChange={(value) => setSubmenuForm({ ...submenuForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmenuDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveSubmenu}>
              <Save size={14} className="mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuManagement;
